/**
 * Hero slider — vanilla, no jQuery.
 *
 * Single-slide-per-view, infinite loop via head/tail clones, autoplay with
 * pause on hover / page hide, dot navigation, keyboard arrows, touch swipe,
 * respects prefers-reduced-motion. CSS reserves the slide box with
 * aspect-ratio so layout is stable before JS attaches.
 */

'use strict';

var SWIPE_THRESHOLD = 40; // px, horizontal swipe distance to navigate
var DEFAULT_AUTOPLAY_MS = 4000;
var RESIZE_DEBOUNCE_MS = 100;

function initHeroSlider(root) {
  var track = root.querySelector('[data-hb-track]');
  if (!track) return;

  var realSlides = Array.prototype.slice.call(track.children);
  var slideCount = realSlides.length;
  if (slideCount === 0) return;

  var dotsContainer = root.querySelector('.hb-dots');
  var dots = dotsContainer ? Array.prototype.slice.call(dotsContainer.querySelectorAll('[data-hb-dot]')) : [];

  var autoplayMs = parseInt(root.getAttribute('data-autoplay-ms'), 10);
  if (!autoplayMs || autoplayMs < 1000) autoplayMs = DEFAULT_AUTOPLAY_MS;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Single slide → nothing to animate, bail after marking it visible.
  if (slideCount < 2) {
    realSlides[0].removeAttribute('aria-hidden');
    return;
  }

  // Append clones front + back to give a seamless infinite loop without
  // mid-track jumps. Clones are ARIA-hidden — screen readers see only the
  // canonical N slides.
  var firstClone = realSlides[0].cloneNode(true);
  var lastClone = realSlides[slideCount - 1].cloneNode(true);
  firstClone.setAttribute('aria-hidden', 'true');
  firstClone.setAttribute('data-hb-clone', 'true');
  lastClone.setAttribute('aria-hidden', 'true');
  lastClone.setAttribute('data-hb-clone', 'true');
  track.appendChild(firstClone);
  track.insertBefore(lastClone, realSlides[0]);

  // After cloning, the first real slide sits at index 1.
  var currentIndex = 1;
  var totalSlides = slideCount + 2;
  var autoplayTimer = null;
  var isPaused = false;
  var pointerStartX = 0;
  var pointerStartY = 0;
  var pointerActive = false;

  function applyTransform(withTransition) {
    if (!withTransition) {
      track.classList.add('is-snapping');
    } else {
      track.classList.remove('is-snapping');
    }
    track.style.transform = 'translate3d(' + (-currentIndex * 100) + '%, 0, 0)';
  }

  function realIndex() {
    if (currentIndex === 0) return slideCount - 1;
    if (currentIndex === totalSlides - 1) return 0;
    return currentIndex - 1;
  }

  function updateAria() {
    var real = realIndex();
    realSlides.forEach(function (slide, i) {
      if (i === real) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
      var link = slide.querySelector('.hb-slide-link');
      if (link) link.setAttribute('tabindex', i === real ? '0' : '-1');
    });
    dots.forEach(function (dot, i) {
      var selected = i === real;
      dot.setAttribute('aria-selected', selected ? 'true' : 'false');
      dot.setAttribute('tabindex', selected ? '0' : '-1');
    });
  }

  function goTo(index, animate) {
    // Safety net for a missed transitionend. In a backgrounded or occluded tab
    // the browser stops running the slide transition, so the transitionend that
    // normally snaps a clone back to its real twin never fires. Without this,
    // autoplay would keep incrementing the index every tick and translate the
    // track ever further off-screen, leaving the empty slide box behind (the
    // "banner disappears to white space after leaving the tab for a while"
    // report). Folding an overshoot back into the real slides, without
    // animation, keeps the slider on a real slide no matter how many ticks were
    // missed. The normal path still reaches the clone at totalSlides - 1 and
    // lets transitionend do the seamless snap; this only catches true overshoot.
    if (index > totalSlides - 1) {
      index = 1 + ((index - 1) % slideCount);
      animate = false;
    } else if (index < 0) {
      index = totalSlides - 2;
      animate = false;
    }
    currentIndex = index;
    applyTransform(animate !== false && !reducedMotion);
    updateAria();
  }

  function next() {
    goTo(currentIndex + 1, true);
  }

  function prev() {
    goTo(currentIndex - 1, true);
  }

  // On transitionend, if we landed on a clone, snap back to its real twin
  // without animation so the user sees a seamless loop.
  track.addEventListener('transitionend', function () {
    if (currentIndex === totalSlides - 1) {
      goTo(1, false);
    } else if (currentIndex === 0) {
      goTo(totalSlides - 2, false);
    }
  });

  function startAutoplay() {
    if (reducedMotion || isPaused) return;
    stopAutoplay();
    autoplayTimer = window.setInterval(next, autoplayMs);
  }

  function stopAutoplay() {
    if (autoplayTimer) {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function resetAutoplay() {
    stopAutoplay();
    startAutoplay();
  }

  // Hover pause (desktop only — touch devices fire mouseenter too, but the
  // pointer-leave fires on tap-end so it self-corrects).
  root.addEventListener('mouseenter', function () {
    isPaused = true;
    stopAutoplay();
  });
  root.addEventListener('mouseleave', function () {
    isPaused = false;
    startAutoplay();
  });

  // Tab away → pause; tab back → resume.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      stopAutoplay();
    } else if (!isPaused) {
      startAutoplay();
    }
  });

  // Window blur/focus → pause when the browser window itself loses focus, e.g.
  // switching to another application with the tab left open. That case does NOT
  // fire visibilitychange (the tab is still "visible"), so without this,
  // autoplay would keep ticking against a tab the browser has stopped painting.
  // Pausing here stops the wasted work; the goTo guard above is what actually
  // prevents the runaway if a tick ever does slip through.
  window.addEventListener('blur', function () {
    stopAutoplay();
  });
  window.addEventListener('focus', function () {
    if (!isPaused && !document.hidden) {
      startAutoplay();
    }
  });

  // Dot clicks
  dots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      goTo(i + 1, true);
      resetAutoplay();
    });
  });

  // Keyboard arrows when focus is anywhere inside the slider.
  root.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
      resetAutoplay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
      resetAutoplay();
    }
  });

  // Touch + pointer swipe. Using pointer events for unified mouse/touch/pen.
  function onPointerDown(e) {
    pointerActive = true;
    pointerStartX = e.clientX;
    pointerStartY = e.clientY;
  }
  function onPointerUp(e) {
    if (!pointerActive) return;
    pointerActive = false;
    var dx = e.clientX - pointerStartX;
    var dy = e.clientY - pointerStartY;
    // Ignore vertical scrolls — only act on a clearly horizontal swipe.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) next(); else prev();
    resetAutoplay();
  }
  function onPointerCancel() {
    pointerActive = false;
  }

  if (window.PointerEvent) {
    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('pointerup', onPointerUp);
    root.addEventListener('pointercancel', onPointerCancel);
  } else {
    // Older Safari fallback
    root.addEventListener('touchstart', function (e) {
      onPointerDown(e.touches[0]);
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      onPointerUp(e.changedTouches[0]);
    });
  }

  // Resize: aspect-ratio handles the layout, but we recompute the transform
  // in case the browser ever needs it (defensive — debounced).
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      applyTransform(false);
    }, RESIZE_DEBOUNCE_MS);
  });

  // Initial state
  applyTransform(false);
  updateAria();
  startAutoplay();
}

module.exports = function initAllHeroSliders() {
  var sliders = document.querySelectorAll('[data-hb-slider]');
  for (var i = 0; i < sliders.length; i++) {
    initHeroSlider(sliders[i]);
  }
};
